import React, { useEffect, useState } from "react";
import { message } from "antd";
import Lottie from "lottie-react";
import styles from "./SocketLoader.module.scss";
import loading from "../../Json/loading.json";
import { storageKeys } from "../../constants/storageKeys";
import { socketActionType, socketStatus } from "../../enums/socketEnums";
import { loaderConsts, loaderMessages, REFRESH_WARNING } from "./constants";
import ConditionalRender from "../ConditionalRender";
import { appRoutes } from "../../constants/appRoutes";
import { directDownloadUtil, downloadUtil } from "../../utils/downloadUtil";

export interface SocketLoaderProps {
  actionType: socketActionType;
  handleActionCall: () => Promise<void>;
  closeRender: () => void;
  handleCallback?: () => void;
  successCallback?: () => void;
}

const SocketLoader = ({
  actionType,
  handleActionCall,
  closeRender,
  handleCallback,
  successCallback,
}: SocketLoaderProps) => {
  const [loaderVisible, isLoaderVisible] = useState<socketStatus>(
    socketStatus.CONNECTING,
  );
  const [syncStarted, setSyncStarted] = useState<boolean>(false);
  const [downloadStarted, setDownloadStarted] = useState<boolean>(false);
  const [downloadMessage, setDownloadMessage] = useState<string>("");
  const [generationStarted, setGenerationStarted] = useState<boolean>(false);
  const [processingRequestComplete, setProcessingRequestComplete] =
    useState<boolean>(false);

  useEffect(() => {
    const wsbaseUrl = process.env.REACT_APP_SOCKET_GATEWAY;
    const sessionParam = sessionStorage.getItem(storageKeys.SESSION_TOKEN);
    const wsUrl = `${wsbaseUrl}?token=${sessionParam}`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = async () => {
      isLoaderVisible(socketStatus.IDLE);
      await handleActionCall()
        ?.then((res) => {})
        ?.catch((err) => {
          ws.close();
          message.error("Unable to Initiate Action");
          throw new Error(`WS error!: ${err}`);
        });
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (
          actionType === socketActionType.SYNC_QUICKBOOKS ||
          actionType === socketActionType.SYNC_FOR_CUSTOMER_DATABASE ||
          actionType === socketActionType.SYNC_FOR_QUARRY_DATABASE
        ) {
          if (data?.event !== actionType) {
            ws.close();
            message.error("Event Key Mismatch");
            return;
          }
          if (data?.status === loaderConsts.ERROR) {
            ws.close();
            message.error(loaderConsts.SYNC_FAILED_MSG);
          } else if (data?.status === loaderConsts.START) {
            setSyncStarted(true);
          } else if (data?.status === loaderConsts.COMPLETED) {
            handleCallback?.(
              actionType === socketActionType.SYNC_FOR_CUSTOMER_DATABASE ||
                actionType === socketActionType.SYNC_FOR_QUARRY_DATABASE
                ? data?.message
                : data?.data,
            );
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
          }
        } else if (actionType === socketActionType.DOWNLOAD_ALL_UNPAID) {
          if (data?.event !== actionType) {
            ws.close();
            message.error("Event Key Mismatch");
            return;
          }
          if (data?.status === loaderConsts.ERROR) {
            ws.close();
            if (
              data?.message === loaderConsts.DOCUMENTS_NOT_GENERATED ||
              !!data?.message
            )
              message.warning(data?.message);
            else message.error(loaderConsts.DOWNLOAD_BATCH_FAILED);
          } else if (
            data?.status === loaderConsts.START ||
            data?.status === loaderConsts.PROCESSING
          ) {
            setDownloadStarted(true);
            setDownloadMessage(data?.message);
          } else if (data?.status === loaderConsts.COMPLETED) {
            handleCallback?.();
            window.removeEventListener("beforeunload", handleBeforeUnload);
            directDownloadUtil(data?.url);
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
          }
        } else if (actionType === socketActionType.GENERATE_BATCH_DOCUMENTS) {
          if (data?.status === loaderConsts.START) {
            setGenerationStarted(true);
          } else if (data?.status === loaderConsts.COMPLETED) {
            successCallback?.();
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
          } else if (data?.status === loaderConsts.ERROR) {
            message.warn(data?.message);
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
          }
        } else if (actionType === socketActionType.RECALL_INVOICE) {
          if (data?.event !== actionType) {
            ws.close();
            message.error("Event Key Mismatch");
            return;
          }
          if (data?.status === loaderConsts.ERROR) {
            ws.close();
            if (!!data?.message) message.warning(data?.message);
            else message.error(loaderConsts.ERROR);
          } else if (data?.status === loaderConsts.START) {
            setProcessingRequestComplete(true);
          } else if (data?.status === loaderConsts.COMPLETED) {
            handleCallback?.();
            if (!!data?.url) downloadUtil(data?.url);
            if (!!data?.message) message.success(data?.message);
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
          }
        } else {
          if (
            data?.event === actionType &&
            data?.status &&
            data?.status === loaderConsts.ERROR
          ) {
            closeRender();
            if (data?.message) {
              message?.error(data?.message);
            } else message.error("Error unable to perform request action");
          } else if (
            data?.event === actionType &&
            data?.status &&
            data?.status === loaderConsts.START
          ) {
            setProcessingRequestComplete(true);
          } else if (data?.event === actionType && data?.url) {
            const link = document.createElement("a");
            link.href = data?.url;
            let pathname = new URL(data?.url)?.pathname;
            let filename = decodeURIComponent(pathname?.split("/").pop());

            link.setAttribute("download", filename);

            window.removeEventListener("beforeunload", handleBeforeUnload);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.addEventListener("beforeunload", handleBeforeUnload);

            isLoaderVisible(socketStatus.SUCCESS);
            message.success(loaderConsts.DOWNLOAD_SUCCESS);
            ws.close();
            isLoaderVisible(socketStatus.CLOSED);
            closeRender();
            successCallback?.();
          } else {
            ws.close();
            if (data?.event !== actionType) message.error("Event Key Mismatch");
            else message.error("Data missing");
          }
        }
      } catch (err) {
        ws.close();
        console.error("WebSocket message error:", err);
      }
    };

    ws.onerror = (err) => {
      message.error("Websocket Error");
      console.error("WebSocket error:", err);
    };

    ws.onclose = (event) => {
      closeRender();
    };

    //handle refresh
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    const handlePopState = () => {
      const confirmed = window.confirm(REFRESH_WARNING);
      if (confirmed) {
        window.location.href = appRoutes.DASHBOARD;
        ws.close();
        isLoaderVisible(socketStatus.CLOSED);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    window.history.pushState(null, "", window.location.href);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      ws.close();
      isLoaderVisible(socketStatus.CLOSED);
    };
  }, []);

  return (
    <ConditionalRender
      visible={
        loaderVisible === socketStatus.CONNECTING ||
        loaderVisible === socketStatus.IDLE
      }
    >
      <div className={styles.LoaderContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.lottieContainer}>
            <Lottie loop={true} autoPlay={true} animationData={loading} />
            <h2 className={styles.textContainer}>
              <ConditionalRender
                visible={
                  actionType !== socketActionType.DOWNLOAD_ALL_UNPAID &&
                  actionType !== socketActionType.GENERATE_BATCH_DOCUMENTS &&
                  actionType !== socketActionType.DOWNLOAD_ORIGINAL &&
                  actionType !== socketActionType.RECALL_INVOICE
                }
              >
                {actionType === socketActionType.SYNC_QUICKBOOKS ||
                actionType === socketActionType.SYNC_FOR_CUSTOMER_DATABASE ||
                actionType === socketActionType.SYNC_FOR_QUARRY_DATABASE
                  ? syncStarted
                    ? loaderMessages.STARTED_SYNC_PROCESS
                    : loaderMessages.PROCESSING_SYNC_REQUEST
                  : actionType === socketActionType.DOWNLOAD_INVOICE
                    ? loaderMessages.DOWNLOAD_MSG
                    : loaderMessages.EXPORT_MSG}
              </ConditionalRender>
              <ConditionalRender
                visible={actionType === socketActionType.DOWNLOAD_ALL_UNPAID}
              >
                {downloadStarted
                  ? downloadMessage || loaderMessages.DOWNLOAD_MSG
                  : loaderMessages.INIATING_DOWNLOAD}
              </ConditionalRender>
              <ConditionalRender
                visible={
                  actionType === socketActionType.GENERATE_BATCH_DOCUMENTS
                }
              >
                {generationStarted
                  ? loaderMessages.GENERATION_PROCESSING
                  : loaderMessages.GENERATION_INITATED}
              </ConditionalRender>
              <ConditionalRender
                visible={actionType === socketActionType.RECALL_INVOICE}
              >
                {processingRequestComplete
                  ? loaderMessages.RECALL_IN_PROCESS
                  : loaderMessages.PROCESSING_REQUEST}
              </ConditionalRender>
              <ConditionalRender
                visible={actionType === socketActionType.DOWNLOAD_ORIGINAL}
              >
                {processingRequestComplete
                  ? loaderMessages.DOWNLOAD_ORIGINAL
                  : loaderMessages.PROCESSING_REQUEST}
              </ConditionalRender>
            </h2>
          </div>
        </div>
      </div>
    </ConditionalRender>
  );
};

export default SocketLoader;
